import React, { useRef, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Camera, ImageIcon, X, Crop, Sparkles } from './Icons';
import { API } from '../contexts/AuthContext';

/* ─── Types ──────────────────────────────────────────────────── */
interface CropBox { x: number; y: number; w: number; h: number } // pixels on displayed image

type DragType = 'move' | 'tl' | 'tr' | 'bl' | 'br';
interface DragState {
  type: DragType;
  startX: number; startY: number;
  startBox: CropBox;
}

/* ─── Pinterest Tab ───────────────────────────────────────────── */
interface PBoard { id: string; name: string; coverImage: string | null }
interface PPin   { id: string; title: string; imageUrl: string }

interface PinterestTabProps { onPicked: (file: File, url: string) => void }

const PINTEREST_BOARDS_CACHE_TTL_MS = 5 * 60 * 1000;

let pinterestBoardsCache: { boards: PBoard[]; expiresAt: number } | null = null;
let pinterestBoardsRequest: Promise<PBoard[]> | null = null;

async function loadPinterestBoards(forceRefresh = false): Promise<PBoard[]> {
  const now = Date.now();
  if (!forceRefresh && pinterestBoardsCache && pinterestBoardsCache.expiresAt > now) {
    return pinterestBoardsCache.boards;
  }

  if (!forceRefresh && pinterestBoardsRequest) {
    return pinterestBoardsRequest;
  }

  pinterestBoardsRequest = axios
    .get(`${API}/pinterest/boards`)
    .then(({ data }) => {
      const boards = data.boards as PBoard[];
      pinterestBoardsCache = { boards, expiresAt: Date.now() + PINTEREST_BOARDS_CACHE_TTL_MS };
      return boards;
    })
    .finally(() => {
      pinterestBoardsRequest = null;
    });

  return pinterestBoardsRequest;
}

function invalidatePinterestBoardsCache() {
  pinterestBoardsCache = null;
}

function PinterestTab({ onPicked }: PinterestTabProps) {
  type Status = 'idle' | 'connecting' | 'connected' | 'expired' | 'error';
  const [status, setStatus]           = useState<Status>('idle');
  const [boards, setBoards]           = useState<PBoard[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<string | null>(null);
  const [pins, setPins]               = useState<PPin[]>([]);
  const [loadingPins, setLoadingPins] = useState(false);
  const [importingPin, setImportingPin] = useState<string | null>(null);
  const [error, setError]             = useState('');
  const authResolvedRef = useRef(false);

  const fetchBoards = useCallback(async (forceRefresh = false) => {
    setLoadingBoards(true);
    setError('');
    try {
      const nextBoards = await loadPinterestBoards(forceRefresh);
      setBoards(nextBoards);
      setStatus('connected');
    } catch (err: any) {
      if (err.response?.status === 401) {
        setStatus('idle');
      } else if (err.response?.status === 422) {
        setStatus('expired');
      } else {
        const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to load boards';
        setError(msg);
        setStatus('error');
      }
    } finally {
      setLoadingBoards(false);
    }
  }, []);

  useEffect(() => { fetchBoards(); }, [fetchBoards]);

  const handleConnect = () => {
    setStatus('connecting');
    setError('');
    authResolvedRef.current = false;

    // iOS Safari (and most popup blockers) only honor window.open when it is
    // invoked synchronously inside the user-gesture handler. Awaiting the
    // auth-url request first would drop us out of the trusted gesture context
    // and silently block the popup. So open the window first with a blank
    // placeholder, then navigate it once the auth URL arrives.
    const popup = window.open('about:blank', 'pinterest_oauth', 'width=620,height=700,left=200,top=100');
    if (!popup || popup.closed) {
      setStatus('error');
      setError('Could not open Pinterest. Please allow pop-ups for this site and try again.');
      return;
    }

    try {
      popup.document.write(
        '<!DOCTYPE html><html><head><title>Pinterest</title>' +
        '<meta name="viewport" content="width=device-width,initial-scale=1">' +
        '<style>body{font-family:-apple-system,system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#555}</style>' +
        '</head><body>Connecting to Pinterest…</body></html>'
      );
    } catch {
      // Some browsers (or cross-origin states) disallow document.write on the placeholder.
      // Safe to ignore — the popup will simply remain blank until navigation.
    }

    let pollClosed: ReturnType<typeof setInterval> | null = null;

    const cleanup = () => {
      window.removeEventListener('message', handler);
      if (pollClosed) {
        clearInterval(pollClosed);
        pollClosed = null;
      }
    };

    const handler = (e: MessageEvent) => {
      if (e.data === 'pinterest:connected') {
        authResolvedRef.current = true;
        invalidatePinterestBoardsCache();
        cleanup();
        popup.close();
        setStatus('connected');
        fetchBoards(true);
      } else if (e.data === 'pinterest:error') {
        authResolvedRef.current = true;
        cleanup();
        popup.close();
        setStatus('error');
        setError('Pinterest authorization failed. Please try again.');
      }
    };
    window.addEventListener('message', handler);
    pollClosed = setInterval(() => {
      if (popup.closed) {
        cleanup();
        if (!authResolvedRef.current) setStatus('idle');
      }
    }, 800);

    axios
      .get(`${API}/pinterest/auth-url`)
      .then(({ data }) => {
        if (popup.closed) return;
        popup.location.href = data.authUrl;
      })
      .catch((err: any) => {
        cleanup();
        if (!popup.closed) popup.close();
        setStatus('error');
        setError(err.response?.data?.error || err.response?.data?.message || 'Could not start Pinterest authorization');
      });
  };

  const handleSelectBoard = async (boardId: string) => {
    setSelectedBoard(boardId);
    setPins([]);
    setLoadingPins(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/pinterest/boards/${boardId}/pins`);
      setPins(data.pins);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load pins');
    } finally {
      setLoadingPins(false);
    }
  };

  const handlePinClick = async (pin: PPin) => {
    setImportingPin(pin.id);
    try {
      const resp = await fetch(`${API}/proxy/image?url=${encodeURIComponent(pin.imageUrl)}`);
      if (!resp.ok) throw new Error('Failed to fetch pin image');
      const blob = await resp.blob();
      const file = new File([blob], `pinterest-pin-${pin.id}.jpg`, { type: blob.type || 'image/jpeg' });
      onPicked(file, URL.createObjectURL(blob));
    } catch (err: any) {
      setError(err.message || 'Could not import pin');
    } finally {
      setImportingPin(null);
    }
  };

  if (status === 'idle' || status === 'connecting' || status === 'expired') {
    return (
      <div className="pinterest-connect-section">
        <PinterestIcon size={40} />
        {status === 'expired'
          ? <p>Your Pinterest session expired. Reconnect to continue browsing boards.</p>
          : <p>Connect your Pinterest account to browse boards and import pin images.</p>
        }
        {error && <p style={{ color: 'var(--error)', fontSize: '0.8125rem' }}>{error}</p>}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ minWidth: 180 }}
          onClick={handleConnect}
          disabled={status === 'connecting'}
        >
          {status === 'connecting'
            ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Opening Pinterest…</>
            : status === 'expired'
            ? <><PinterestIcon size={16} /> Reconnect Pinterest</>
            : <><PinterestIcon size={16} /> Connect Pinterest</>}
        </button>
      </div>
    );
  }

  if (loadingBoards) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
        <span className="spinner" />
      </div>
    );
  }

  if (selectedBoard) {
    const board = boards.find(b => b.id === selectedBoard);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button type="button" className="pinterest-back-btn" onClick={() => { setSelectedBoard(null); setPins([]); }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          {board?.name ?? 'Back'}
        </button>
        {error && <p style={{ color: 'var(--error)', fontSize: '0.8125rem', margin: 0 }}>{error}</p>}
        {loadingPins ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <span className="spinner" />
          </div>
        ) : pins.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '24px 0' }}>No pins found in this board.</p>
        ) : (
          <div className="pinterest-pins-grid">
            {pins.map(pin => (
              <div
                key={pin.id}
                className="pinterest-pin-item"
                onClick={() => handlePinClick(pin)}
                role="button"
                tabIndex={0}
                title={pin.title || 'Select pin'}
                onKeyDown={e => e.key === 'Enter' && handlePinClick(pin)}
                style={{ position: 'relative' }}
              >
                <img src={pin.imageUrl} alt={pin.title || 'Pin'} loading="lazy" />
                {importingPin === pin.id && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="spinner" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {error && <p style={{ color: 'var(--error)', fontSize: '0.8125rem', margin: 0 }}>{error}</p>}
      {boards.length === 0 ? (
        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '24px 0' }}>No boards found on your Pinterest account.</p>
      ) : (
        <div className="pinterest-boards-grid">
          {boards.map(board => (
            <div
              key={board.id}
              className="pinterest-board-card"
              onClick={() => handleSelectBoard(board.id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleSelectBoard(board.id)}
            >
              {board.coverImage
                ? <img src={board.coverImage} alt={board.name} loading="lazy" />
                : <div style={{ height: 90, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PinterestIcon size={24} /></div>
              }
              <span>{board.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Pinterest Image Picker ───────────────────────────────── */
interface PinterestPickerProps {
  onPicked: (file: File, url: string) => void;
  onClose: () => void;
}
function PinterestPickerModal({ onPicked, onClose }: PinterestPickerProps) {
  useEffect(() => {
    loadPinterestBoards().catch(() => {});
  }, []);

  return (
    <div className="sheet-backdrop center" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-card" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-card-header">
          <span className="modal-card-title">Import from Pinterest</span>
          <button type="button" className="header-action-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="modal-card-body" style={{ gap: 12, display: 'flex', flexDirection: 'column' }}>
          <PinterestTab onPicked={onPicked} />
        </div>
      </div>
    </div>
  );
}

/* ─── Generated Image Picker Modal ──────────────────────────── */
interface GeneratedImage {
  id: string;
  projectId: string;
  resultImageUrl: string;
  productDescription: string;
}

interface GeneratedImagePickerProps {
  onPicked: (file: File, url: string) => void;
  onClose: () => void;
}

function GeneratedImagePickerModal({ onPicked, onClose }: GeneratedImagePickerProps) {
  const [images, setImages]   = useState<GeneratedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [importing, setImporting] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${API}/generations`)
      .then(res => setImages(res.data))
      .catch(() => setError('Could not load generated images.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (img: GeneratedImage) => {
    setImporting(img.id);
    try {
      const proxyUrl = `${API}/proxy/image?url=${encodeURIComponent(img.resultImageUrl)}`;
      const resp = await fetch(proxyUrl);
      if (!resp.ok) throw new Error('Failed to fetch');
      const blob = await resp.blob();
      const file = new File([blob], `generated-${img.id}.jpg`, { type: blob.type || 'image/jpeg' });
      const objectUrl = URL.createObjectURL(blob);
      onPicked(file, objectUrl);
    } catch {
      setError('Could not load selected image. Try again.');
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className="sheet-backdrop center" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-card" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-card-header">
          <span className="modal-card-title">Your Generated Images</span>
          <button type="button" className="header-action-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="modal-card-body" style={{ padding: 12 }}>
          {loading ? (
            <div style={{ padding: '40px 0', display: 'flex', justifyContent: 'center' }}>
              <div className="spinner" style={{ width: 28, height: 28 }} />
            </div>
          ) : error ? (
            <p style={{ color: 'var(--error)', fontSize: '0.875rem', textAlign: 'center', padding: '20px 0' }}>{error}</p>
          ) : images.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)' }}>
              <ImageIcon size={32} />
              <p style={{ marginTop: 8, fontSize: '0.875rem' }}>No generated images yet.</p>
            </div>
          ) : (
            <div className="gen-picker-grid">
              {images.map(img => (
                <button
                  key={img.id}
                  type="button"
                  className="gen-picker-item"
                  onClick={() => handleSelect(img)}
                  disabled={!!importing}
                  title={img.productDescription}
                >
                  <img src={img.resultImageUrl} alt={img.productDescription} loading="lazy" />
                  {importing === img.id && (
                    <div className="gen-picker-loading">
                      <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="modal-card-footer">
          <button type="button" className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Source Picker Sheet ─────────────────────────────────────── */
interface SourcePickerProps {
  onCamera: () => void;
  onGallery: () => void;
  onPinterest: () => void;
  onFromLibrary: () => void;
  onClose: () => void;
}
function SourcePickerSheet({ onCamera, onGallery, onPinterest, onFromLibrary, onClose }: SourcePickerProps) {
  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Choose image source">
      <div className="bottom-sheet" onClick={e => e.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="sheet-header">
          <span className="sheet-title">Add Photo</span>
          <button type="button" className="header-action-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="sheet-body">
          <div className="sheet-option" onClick={onCamera} role="button" tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onCamera()}>
            <div className="sheet-option-icon"><Camera size={22} /></div>
            <div className="sheet-option-text">
              <strong>Open Camera</strong>
              <span>Take a photo with your camera</span>
            </div>
          </div>
          <div className="sheet-option" onClick={onGallery} role="button" tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onGallery()}>
            <div className="sheet-option-icon"><ImageIcon size={22} /></div>
            <div className="sheet-option-text">
              <strong>Upload from Gallery</strong>
              <span>Choose from your photo library</span>
            </div>
          </div>
          <div className="sheet-option" onClick={onPinterest} role="button" tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onPinterest()}>
            <div className="sheet-option-icon" style={{ color: '#e60023' }}><PinterestIcon size={22} /></div>
            <div className="sheet-option-text">
              <strong>Import from Pinterest</strong>
              <span>Browse your boards and import a pin</span>
            </div>
          </div>
          <div className="sheet-option" onClick={onFromLibrary} role="button" tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onFromLibrary()}>
            <div className="sheet-option-icon" style={{ color: 'var(--accent)' }}><Sparkles size={22} /></div>
            <div className="sheet-option-text">
              <strong>From Generated Images</strong>
              <span>Use a previously AI-generated image</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PinterestIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#e60023', flexShrink: 0 }}>
      <path d="M12 0C5.4 0 0 5.4 0 12c0 5 3.1 9.3 7.5 11.1-.1-.9-.2-2.3 0-3.3.2-.9 1.4-5.8 1.4-5.8s-.4-.7-.4-1.8c0-1.7 1-3 2.2-3 1 0 1.5.8 1.5 1.7 0 1-.7 2.6-1 4-.3 1.2.6 2.2 1.8 2.2 2.1 0 3.7-2.2 3.7-5.4 0-2.8-2-4.8-4.9-4.8-3.3 0-5.3 2.5-5.3 5.1 0 1 .4 2 .9 2.6.1.1.1.2.1.4-.1.4-.3 1.2-.3 1.3-.1.2-.2.3-.4.2-1.5-.7-2.5-2.9-2.5-4.7 0-3.8 2.8-7.3 8-7.3 4.2 0 7.5 3 7.5 7 0 4.2-2.6 7.5-6.3 7.5-1.2 0-2.4-.6-2.8-1.3l-.8 2.9c-.3 1.1-1 2.5-1.5 3.3.6.2 1.1.3 1.7.3C18.6 24 24 18.6 24 12S18.6 0 12 0z"/>
    </svg>
  );
}

/* ─── Crop Modal ──────────────────────────────────────────────── */
interface CropModalProps {
  src: string;
  onConfirm: (croppedFile: File, croppedUrl: string) => void;
  onCancel: () => void;
}

function CropModal({ src, onConfirm, onCancel }: CropModalProps) {
  const imgRef       = useRef<HTMLImageElement>(null);
  const wrapRef      = useRef<HTMLDivElement>(null);
  const [box, setBox]   = useState<CropBox | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  /* init box once image loads */
  const handleImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    const dw = img.clientWidth;
    const dh = img.clientHeight;
    const margin = 0.1;
    setBox({ x: dw * margin, y: dh * margin, w: dw * (1 - 2 * margin), h: dh * (1 - 2 * margin) });
  };

  /* clamp helper */
  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
  const MIN_SZ = 40;

  const getDisplaySize = useCallback(() => {
    const img = imgRef.current;
    if (!img) return { dw: 0, dh: 0 };
    return { dw: img.clientWidth, dh: img.clientHeight };
  }, []);

  /* pointer down on box or handles */
  const onPointerDown = (e: React.PointerEvent, type: DragType) => {
    e.preventDefault();
    e.stopPropagation();
    if (!box) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ type, startX: e.clientX, startY: e.clientY, startBox: { ...box } });
  };

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!drag || !box) return;
    const { dw, dh } = getDisplaySize();
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const sb = drag.startBox;
    let { x, y, w, h } = sb;

    if (drag.type === 'move') {
      x = clamp(sb.x + dx, 0, dw - sb.w);
      y = clamp(sb.y + dy, 0, dh - sb.h);
      setBox({ x, y, w: sb.w, h: sb.h });
    } else {
      if (drag.type === 'tl') {
        const nx = clamp(sb.x + dx, 0, sb.x + sb.w - MIN_SZ);
        const ny = clamp(sb.y + dy, 0, sb.y + sb.h - MIN_SZ);
        setBox({ x: nx, y: ny, w: sb.w - (nx - sb.x), h: sb.h - (ny - sb.y) });
      } else if (drag.type === 'tr') {
        const ny = clamp(sb.y + dy, 0, sb.y + sb.h - MIN_SZ);
        const nw = clamp(sb.w + dx, MIN_SZ, dw - sb.x);
        setBox({ x: sb.x, y: ny, w: nw, h: sb.h - (ny - sb.y) });
      } else if (drag.type === 'bl') {
        const nx = clamp(sb.x + dx, 0, sb.x + sb.w - MIN_SZ);
        const nh = clamp(sb.h + dy, MIN_SZ, dh - sb.y);
        setBox({ x: nx, y: sb.y, w: sb.w - (nx - sb.x), h: nh });
      } else if (drag.type === 'br') {
        const nw = clamp(sb.w + dx, MIN_SZ, dw - sb.x);
        const nh = clamp(sb.h + dy, MIN_SZ, dh - sb.y);
        setBox({ x: sb.x, y: sb.y, w: nw, h: nh });
      }
    }
  }, [drag, box, getDisplaySize]);

  const onPointerUp = () => setDrag(null);

  /* confirm: draw cropped region to canvas */
  const handleConfirm = () => {
    if (!box || !imgRef.current) return;
    const img      = imgRef.current;
    const dw       = img.clientWidth;
    const dh       = img.clientHeight;
    const scaleX   = naturalSize.w / dw;
    const scaleY   = naturalSize.h / dh;
    const sx       = box.x * scaleX;
    const sy       = box.y * scaleY;
    const sw       = box.w * scaleX;
    const sh       = box.h * scaleY;
    const canvas   = document.createElement('canvas');
    canvas.width   = sw;
    canvas.height  = sh;
    const ctx      = canvas.getContext('2d')!;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
      const url  = URL.createObjectURL(blob);
      onConfirm(file, url);
    }, 'image/jpeg', 0.92);
  };

  return (
    <div
      className="sheet-backdrop center"
      role="dialog"
      aria-modal="true"
      aria-label="Crop image"
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <div className="modal-card" style={{ maxWidth: 480 }}>
        <div className="modal-card-header">
          <span className="modal-card-title">Crop Photo</span>
          <button type="button" className="header-action-btn" onClick={onCancel} aria-label="Cancel">
            <X size={18} />
          </button>
        </div>
        <div className="modal-card-body" style={{ padding: '12px' }}>
          <div className="crop-wrapper">
            <div ref={wrapRef} className="crop-area">
              <img
                ref={imgRef}
                src={src}
                alt="Crop preview"
                onLoad={handleImgLoad}
                draggable={false}
                style={{ display: 'block', maxWidth: '100%', maxHeight: '60dvh' }}
              />
              {box && (
                <>
                  <div className="crop-dim" style={{ top: 0, left: 0, right: 0, height: box.y }} />
                  <div className="crop-dim" style={{ top: box.y + box.h, left: 0, right: 0, bottom: 0 }} />
                  <div className="crop-dim" style={{ top: box.y, left: 0, width: box.x, height: box.h }} />
                  <div className="crop-dim" style={{ top: box.y, left: box.x + box.w, right: 0, height: box.h }} />
                  <div
                    className="crop-box"
                    style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
                    onPointerDown={e => onPointerDown(e, 'move')}
                  >
                    <div className="crop-handle tl" onPointerDown={e => { e.stopPropagation(); onPointerDown(e, 'tl'); }} />
                    <div className="crop-handle tr" onPointerDown={e => { e.stopPropagation(); onPointerDown(e, 'tr'); }} />
                    <div className="crop-handle bl" onPointerDown={e => { e.stopPropagation(); onPointerDown(e, 'bl'); }} />
                    <div className="crop-handle br" onPointerDown={e => { e.stopPropagation(); onPointerDown(e, 'br'); }} />
                  </div>
                </>
              )}
            </div>
          </div>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-faint)', textAlign: 'center', marginTop: 10 }}>
            Drag the corners to adjust the crop area
          </p>
        </div>
        <div className="modal-card-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel} style={{ flex: 1 }}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleConfirm} style={{ flex: 2 }}>
            <Crop size={16} /> Use This Crop
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main ImagePicker ────────────────────────────────────────── */
export interface PickedImage { file: File; url: string }

interface ImagePickerProps {
  value?: PickedImage | null;
  onChange: (img: PickedImage) => void;
  onRemove?: () => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  locked?: boolean;        // once set, cannot be changed
  required?: boolean;
  id?: string;
}

export function ImagePicker({
  value,
  onChange,
  onRemove,
  label,
  hint,
  disabled = false,
  locked = false,
  required = false,
  id,
}: ImagePickerProps) {
  const cameraRef  = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const [showSource, setShowSource]     = useState(false);
  const [showPinterestPicker, setShowPinterestPicker] = useState(false);
  const [showGenPicker, setShowGenPicker] = useState(false);
  const [cropSrc, setCropSrc]           = useState<string | null>(null);
  const [pendingFile, setPendingFile]   = useState<File | null>(null);

  const isDisabled = disabled || (locked && !!value);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const url = URL.createObjectURL(file);
    setPendingFile(file);
    setCropSrc(url);
    setShowSource(false);
  };

  const handlePinterestPicked = (file: File, objectUrl: string) => {
    setPendingFile(file);
    setCropSrc(objectUrl);
    setShowPinterestPicker(false);
    setShowSource(false);
  };

  const handleGenPicked = (file: File, objectUrl: string) => {
    setPendingFile(file);
    setCropSrc(objectUrl);
    setShowGenPicker(false);
    setShowSource(false);
  };

  const handleCropConfirm = (croppedFile: File, croppedUrl: string) => {
    onChange({ file: croppedFile, url: croppedUrl });
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPendingFile(null);
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPendingFile(null);
  };

  useEffect(() => {
    return () => { if (cropSrc) URL.revokeObjectURL(cropSrc); };
  }, [cropSrc]);

  return (
    <div className="form-field">
      {label && (
        <label className="form-label" htmlFor={id}>
          {label}{required && <span className="required">*</span>}
        </label>
      )}

      {value ? (
        <div className="image-preview-card">
          <img src={value.url} alt="Selected" style={{ maxHeight: 220, width: '100%', objectFit: 'cover' }} />
          {locked ? (
            <div className="image-preview-locked" title="This image cannot be changed"><Lock size={14} /></div>
          ) : (
            !disabled && onRemove && (
              <button className="image-remove-btn" onClick={onRemove} aria-label="Remove image" type="button">
                <X size={14} />
              </button>
            )
          )}
        </div>
      ) : (
        <button
          type="button"
          className={`upload-zone${isDisabled ? ' disabled' : ''}`}
          onClick={() => !isDisabled && setShowSource(true)}
          disabled={isDisabled}
          id={id}
          aria-label={label || 'Upload image'}
        >
          <div className="upload-zone-icon">
            <Camera size={22} />
          </div>
          <p>{hint || 'Tap to add photo'}</p>
          <small>Camera or Gallery · JPG / PNG</small>
        </button>
      )}

      {hint && !value && (
        <p className="form-helper">{hint}</p>
      )}

      {/* Hidden inputs */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInput}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {showSource && (
        <SourcePickerSheet
          onCamera={() => { setShowSource(false); cameraRef.current?.click(); }}
          onGallery={() => { setShowSource(false); galleryRef.current?.click(); }}
          onPinterest={() => { setShowSource(false); setShowPinterestPicker(true); }}
          onFromLibrary={() => { setShowSource(false); setShowGenPicker(true); }}
          onClose={() => setShowSource(false)}
        />
      )}

      {showPinterestPicker && (
        <PinterestPickerModal
          onPicked={handlePinterestPicked}
          onClose={() => setShowPinterestPicker(false)}
        />
      )}

      {showGenPicker && (
        <GeneratedImagePickerModal
          onPicked={handleGenPicked}
          onClose={() => setShowGenPicker(false)}
        />
      )}

      {cropSrc && (
        <CropModal
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

/* ─── Multi Image Picker ──────────────────────────────────────── */
interface MultiImagePickerProps {
  values: PickedImage[];
  onChange: (images: PickedImage[]) => void;
  label?: string;
  maxImages?: number;
  disabled?: boolean;
  required?: boolean;
}

export function MultiImagePicker({
  values,
  onChange,
  label,
  maxImages = 5,
  disabled = false,
  required = false,
}: MultiImagePickerProps) {
  const cameraRef  = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const [showSource, setShowSource]       = useState(false);
  const [showPinterestPicker, setShowPinterestPicker] = useState(false);
  const [showGenPicker, setShowGenPicker] = useState(false);
  const [cropSrc, setCropSrc]             = useState<string | null>(null);

  const canAdd = !disabled && values.length < maxImages;

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const url = URL.createObjectURL(file);
    setCropSrc(url);
    setShowSource(false);
  };

  const handlePinterestPicked = (file: File, objectUrl: string) => {
    setCropSrc(objectUrl);
    setShowPinterestPicker(false);
    setShowSource(false);
  };

  const handleGenPicked = (file: File, objectUrl: string) => {
    setCropSrc(objectUrl);
    setShowGenPicker(false);
    setShowSource(false);
  };

  const handleCropConfirm = (croppedFile: File, croppedUrl: string) => {
    onChange([...values, { file: croppedFile, url: croppedUrl }]);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleRemove = (idx: number) => {
    const next = values.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="form-field">
      {label && (
        <span className="form-label">
          {label}{required && <span className="required">*</span>}
        </span>
      )}

      <div className="ref-images-grid">
        {values.map((img, i) => (
          <div key={i} className="ref-image-item">
            <img src={img.url} alt={`Reference ${i + 1}`} />
            {!disabled && (
              <button
                className="image-remove-btn"
                onClick={() => handleRemove(i)}
                aria-label={`Remove reference image ${i + 1}`}
                type="button"
                style={{ top: 4, right: 4, width: 26, height: 26 }}
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}

        {canAdd && (
          <button
            type="button"
            className="ref-add-btn"
            onClick={() => setShowSource(true)}
            aria-label="Add reference image"
          >
            <Plus size={20} />
            <span>Add</span>
          </button>
        )}
      </div>

      {values.length === 0 && (
        <p className="form-helper">Add photos for AI reference · Up to {maxImages} images</p>
      )}

      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" onChange={handleFileInput} style={{ display: 'none' }} aria-hidden="true" />
      <input ref={galleryRef} type="file" accept="image/*" onChange={handleFileInput} style={{ display: 'none' }} aria-hidden="true" />

      {showSource && (
        <SourcePickerSheet
          onCamera={() => { setShowSource(false); cameraRef.current?.click(); }}
          onGallery={() => { setShowSource(false); galleryRef.current?.click(); }}
          onPinterest={() => { setShowSource(false); setShowPinterestPicker(true); }}
          onFromLibrary={() => { setShowSource(false); setShowGenPicker(true); }}
          onClose={() => setShowSource(false)}
        />
      )}

      {showPinterestPicker && (
        <PinterestPickerModal
          onPicked={handlePinterestPicked}
          onClose={() => setShowPinterestPicker(false)}
        />
      )}

      {showGenPicker && (
        <GeneratedImagePickerModal
          onPicked={handleGenPicked}
          onClose={() => setShowGenPicker(false)}
        />
      )}

      {cropSrc && (
        <CropModal
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

// local Lock icon for locked state
function Lock({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function Plus({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
